import React, { useEffect, useState } from "react";
import { useQuery, useQueries } from "@tanstack/react-query";

import config from "../config/config";
import * as UTILS from "../utils/utils";
import * as QUERIES from "../queries/query-functions";

import { getCustomer } from "../queries/global";

import LoadingIndicator from "../components/loading-indicator";
import AccountNavigation from "./account-navigation";
import UpsellBlock from "../components/upsell-block";
import LogoutButton from "../components/logout-button";
import UpcomingTickets from "./upcoming-tickets";
import ExtrasInstance from "../extras/extras-instance";
import IFrame from "../components/iframe";

const iframeURL = `${config.DOMAIN_NAME}/${config.CLIENT_NAME}/website/secure/myaccount.aspx?screen=orders&resize=true`;

const AccountOrders = () => {
  const { data: customer } = getCustomer();

  const [upcomingTickets, setUpcomingTickets] = useState(null);
  const [extrasPending, setExtrasPending] = useState(null);
  const [extras, setExtras] = useState(null);
  const [currentExtras, setCurrentExtras] = useState(null);

  const { isPending: ordersAuthPending, data: ordersDataAuth } = useQuery({
    queryKey: ["ordersDataAuth", customer?.id],
    enabled: !!customer?.id,
    queryFn: () => QUERIES.getAuthenticatedOrders(customer.id),
    refetchOnWindowFocus: false,
    retry: false,
  });

  const supplementaryEventData = useQueries({
    queries: upcomingTickets
      ? upcomingTickets.map((ticket) => ({
          queryKey: ["supplementaryEventData", ticket.instance.id],
          queryFn: () => QUERIES.getSupplementaryEvents(ticket.instance.id),
          enabled: true,
          staleTime: Infinity,
          refetchOnWindowFocus: false,
          retry: false,
        }))
      : [],
  });

  useEffect(() => {
    if (!ordersDataAuth) return;

    const upcoming = UTILS.getUpcomingTickets(ordersDataAuth);
    setUpcomingTickets(upcoming);
  }, [ordersDataAuth]);

  useEffect(() => {
    if (!supplementaryEventData?.length || !upcomingTickets?.length) return;

    const is_pending = supplementaryEventData.some((query) => query.isLoading);
    setExtrasPending(is_pending);

    const data = supplementaryEventData
      .map((result, index) => {
        if (result.data)
          return {
            instance: upcomingTickets[index],
            extras: result.data,
          };
      })
      .filter((notUndefined) => notUndefined !== undefined);

    // Only set extras if the array has changed to stop infinite rerenders
    setExtras((prev) => {
      const isSame =
        prev?.length === data.length &&
        prev?.every(
          (item, index) =>
            item.instance === data[index].instance &&
            item.extras === data[index].extras
        );
      return isSame ? prev : data;
    });

    if (currentExtras) return;
    setCurrentExtras(data[0]);
  }, [supplementaryEventData]);

  const handleExtrasChange = (e) => {
    if (!e) return;
    const new_extras = extras.find(
      (extra) => extra.instance.id == e.target.value
    );
    setCurrentExtras(new_extras);
  };

  return (
    <>
      <AccountNavigation />

      <div className="booking-pathway__container booking-pathway__account booking-pathway__account--orders">
        <div className="booking-pathway__main-content">
          <h1 className="booking-pathway__header">Orders & tickets</h1>

          <UpcomingTickets
            upcomingTickets={upcomingTickets}
            pending={ordersAuthPending}
          />

          {extrasPending && (
            <section className="orders__extras">
              <header className="orders__extras--header">
                <h2 className="booking-pathway__header">
                  Enhance your experience
                </h2>
              </header>

              <LoadingIndicator />
            </section>
          )}

          {!extrasPending && extras?.length && currentExtras?.extras?.length ? (
            <section className="orders__extras">
              <header className="orders__extras--header">
                <h2 className="booking-pathway__header">
                  Enhance your experience
                </h2>
                {extras.length > 1 ? (
                  <select
                    className="orders__extras--select"
                    name="extras"
                    id="extrasSelect"
                    onChange={handleExtrasChange}
                  >
                    {extras.map((extra) => {
                      const formatted_date = UTILS.formatDateTime(
                        extra.instance?.instance.start
                      );
                      return (
                        <option
                          key={extra.instance?.id}
                          value={extra.instance?.id}
                        >
                          {extra.instance?.instance.event.name} {formatted_date}
                        </option>
                      );
                    })}
                  </select>
                ) : (
                  (() => {
                    const formatted_date = UTILS.formatDateTime(
                      currentExtras?.instance.instance.start
                    );
                    return (
                      <p className="orders__extras--date">
                        {currentExtras?.instance?.instance.event.name}{" "}
                        {formatted_date}
                      </p>
                    );
                  })()
                )}
              </header>

              <div className="extras-group orders__extras--group">
                {currentExtras?.extras?.length
                  ? currentExtras.extras.map((instance) => (
                      <ExtrasInstance
                        key={instance.id}
                        instance={instance}
                        isAccountPage={true}
                      />
                    ))
                  : null}
              </div>
            </section>
          ) : null}

          <section className="past-orders">
            <h2 className="booking-pathway__header">Past orders</h2>

            <IFrame
              url={iframeURL}
              id="SpektrixAccountIFrame"
              title="Orders"
              hasLoader="true"
            />
          </section>
        </div>

        <div className="booking-pathway__footer">
          <LogoutButton />

          <UpsellBlock
            content={config.OPTION_FIELDS.account?.orders_upsell}
            type="orders-upsell"
          />
        </div>
      </div>
    </>
  );
};

export default AccountOrders;
